"use client";

import { Link, useNavigate } from "react-router-dom";
import WestBreeze from "../assets/WestBreeze.png";
import loginImage from "../assets/loginImage.png";
import signIn from "../assets/signIn.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLoginUserMutation } from "../services/Api";
import { useDispatch } from "react-redux";
import { loggedIn } from "../redux/AuthSlice";
import { toast } from "react-toastify";
import { useState } from "react";
import { banner_traveller, login_img } from "../assets/updated_assests/assets";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .email("Email is invalid")
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Email is invalid"),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/^[^\s]+$/, "Password must not contain spaces"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
  });

  const [loginUser, { isLoading: loginUserLoading }] = useLoginUserMutation();

  const onSubmit = (values) => {
    const data = {
      email: values?.email,
      password: values?.password,
    };

    loginUser({ data: data })
      .unwrap()
      .then((payload) => {
        if (payload.success) {
          const response = {
            token: payload?.token,
            user: payload?.user,
          };
          dispatch(loggedIn(response));
          navigate(response?.user?.role == "ADMIN" ? "/dashboard" : "/profile");
          window.location.reload();
        } else {
          toast.error(payload?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.data?.message);
      });
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Black header with mission text */}
      <div className="w-full bg-black py-3 text-center">
        <p className="font-['Roboto_Mono'] font-bold italic text-base text-white">
          Our Mission Is...
        </p>
      </div>

      {/* Navigation bar with green-yellow background */}
      <div className="w-full bg-[#D9DCB0] p-4 md:p-5">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="text-[#ff6b35]">
            <img
              src={WestBreeze || "/placeholder.svg"}
              alt="Westbreeze"
              className="w-[160px]"
            />
          </Link>
          <Link
            to="/sign-up"
            className="bg-transparent border-2  border-[#1a1a1a] px-4 py-3 flex items-center gap-2"
          >
            <span className="text-sm font-bold font-['Quicksand'] flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 17L17 7M17 7H7M17 7V17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Sign Up
            </span>
          </Link>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Left side - Image (now visible on mobile) */}
        <div className="w-full md:w-2/5 h-48 md:h-auto">
          <img
            src={banner_traveller || "/placeholder.svg"}
            alt="Adventure"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Right side - Login form */}
        <div className="w-full md:w-3/5 bg-[#ECE8CE] flex items-center p-6 md:p-24">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h2 className="font-['Roboto_Mono'] font-bold italic text-xl mb-2">
                Welcome Back!
              </h2>
              <p className="font-['Quicksand'] font-medium text-sm text-gray-600">
                Unite with passionate travelers, earn perks on your adventures,
                and unlock.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block font-['Quicksand'] font-medium text-xs mb-2">
                  Email
                </label>
                <input
                  type="email"
                  {...register("email")}
                  className="w-full px-4 py-3 bg-transparent border-2 border-[#1a1a1a] rounded-md focus:outline-none font-['Quicksand'] font-medium text-sm"
                  placeholder="email@example.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block font-['Quicksand'] font-medium text-xs">
                    Password
                  </label>
                  <a
                    href="#"
                    className="text-[#1a1a1a] font-['Quicksand'] font-medium text-xs hover:underline"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register("password")}
                    className="w-full px-4 py-3 bg-transparent border-2 border-[#1a1a1a] rounded-md focus:outline-none pr-10 font-['Quicksand'] font-medium text-sm"
                    placeholder="password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="h-5 w-5" />
                    ) : (
                      <FaEye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center mt-2 px-3 py-3 bg-white border-2 border-[#1a1a1a]  hover:bg-[#d4d8a8] hover:text-black transition-colors gap-2"
                  disabled={loginUserLoading}
                >
                  {loginUserLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Signing In...</span>
                    </>
                  ) : (
                    <>
                      <img
                        src={signIn || "/placeholder.svg"}
                        alt="Sign In"
                        className="h-6"
                      />
                      <span className="font-['Quicksand'] font-bold">
                        Sign In
                      </span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
